import { MoreHorizontal, Pencil, Trash2, RotateCcw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { BaseEntity, EntityConfig, RowActionConfig, SoftDeletableEntity } from './types';
import { UI_STRINGS } from './locale';

interface RowActionsMenuProps<TRecord extends BaseEntity> {
  record: TRecord;
  config: EntityConfig<TRecord>;
  onEdit?: (record: TRecord) => void;
  onSoftDelete?: (record: TRecord) => void;
  onRestore?: (record: TRecord) => void;
  onPermanentDelete?: (record: TRecord) => void;
}

function filterCustomActions<TRecord extends BaseEntity>(
  config: EntityConfig<TRecord>,
  isDeleted: boolean,
): RowActionConfig<TRecord>[] {
  return (
    config.rowActions?.filter((action) => {
      if (action.hideWhenDeleted && isDeleted) {
        return false;
      }
      if (action.showOnlyWhenDeleted && !isDeleted) {
        return false;
      }
      return true;
    }) ?? []
  );
}

function renderCustomActions<TRecord extends BaseEntity>(
  actions: RowActionConfig<TRecord>[],
  record: TRecord,
) {
  return actions.map((action) => (
    <DropdownMenuItem
      key={action.key}
      onClick={(e) => {
        e.stopPropagation();
        void action.onClick(record);
      }}
      className={cn(
        'gap-2',
        action.variant === 'destructive'
          ? 'text-destructive focus:text-destructive'
          : '',
      )}
    >
      {action.icon}
      {action.label}
    </DropdownMenuItem>
  ));
}

/**
 * Выпадающее меню действий для строки таблицы.
 * Показывает стандартные действия (Редактировать, Удалить, Восстановить, Удалить навсегда)
 * и кастомные действия из конфига.
 */
export function RowActionsMenu<TRecord extends BaseEntity>({
  record,
  config,
  onEdit,
  onSoftDelete,
  onRestore,
  onPermanentDelete,
}: RowActionsMenuProps<TRecord>) {
  const softDeletable = record as Partial<SoftDeletableEntity>;
  const isDeleted =
    config.softDelete &&
    softDeletable.deletedAt !== null &&
    softDeletable.deletedAt !== undefined;

  const customActions = filterCustomActions(config, isDeleted);

  const activeCustomActions = !isDeleted ? customActions : [];
  const deletedCustomActions = isDeleted ? customActions : [];

  const showEditAction = config.canEdit && !isDeleted && onEdit !== undefined;
  const showSoftDeleteAction =
    config.softDelete && !isDeleted && onSoftDelete !== undefined;
  const showRestoreAction = isDeleted && onRestore !== undefined;
  const showPermanentDeleteAction = isDeleted && onPermanentDelete !== undefined;

  const hasAnyAction =
    showEditAction ||
    showSoftDeleteAction ||
    showRestoreAction ||
    showPermanentDeleteAction ||
    customActions.length > 0;

  if (!hasAnyAction) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 opacity-0 group-hover/row:opacity-100 focus:opacity-100 data-[state=open]:opacity-100"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">{UI_STRINGS.openActionsMenu}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {/* Редактировать — только для активных записей */}
        {config.canEdit && !isDeleted && onEdit && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onEdit(record);
            }}
            className="gap-2"
          >
            <Pencil className="h-4 w-4" />
            {UI_STRINGS.edit}
          </DropdownMenuItem>
        )}

        {/* Кастомные действия для активных записей */}
        {activeCustomActions.length > 0 && (
          <>
            {showEditAction && <DropdownMenuSeparator />}
            {renderCustomActions(activeCustomActions, record)}
          </>
        )}

        {/* Мягкое удаление — только для активных записей */}
        {config.softDelete && !isDeleted && onSoftDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onSoftDelete(record);
              }}
              className="gap-2 text-amber-600 focus:text-amber-600 dark:text-amber-400 dark:focus:text-amber-400"
            >
              <Trash2 className="h-4 w-4" />
              {UI_STRINGS.delete}
            </DropdownMenuItem>
          </>
        )}

        {/* Восстановить — только для удалённых записей */}
        {isDeleted && onRestore && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onRestore(record);
            }}
            className="gap-2 text-green-600 focus:text-green-600 dark:text-green-400 dark:focus:text-green-400"
          >
            <RotateCcw className="h-4 w-4" />
            {UI_STRINGS.restore}
          </DropdownMenuItem>
        )}

        {/* Кастомные действия для удалённых записей */}
        {deletedCustomActions.length > 0 && (
          <>
            {showRestoreAction && <DropdownMenuSeparator />}
            {renderCustomActions(deletedCustomActions, record)}
          </>
        )}

        {/* Удалить навсегда — только для удалённых записей */}
        {isDeleted && onPermanentDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onPermanentDelete(record);
              }}
              className="gap-2 text-destructive focus:text-destructive"
            >
              <X className="h-4 w-4" />
              {UI_STRINGS.deletePermanently}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
